import { ORGANIZATION_ID } from "@/constants";
import { absoluteUrl } from "@/lib/site";
import { parquePath, type Parque } from "@/content/parques";

/**
 * Structured data for the park guides, built from the same Parque object the
 * page renders (one function per schema block). Rendered inline in JSX so it
 * is present in the prerendered HTML.
 */

export const parqueUrl = (parque: Parque): string =>
  absoluteUrl(parquePath(parque));

export function buildParqueJsonLd(parque: Parque): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "@id": `${parqueUrl(parque)}#atraccion`,
    name: parque.nombreOficial,
    alternateName: parque.atractivoPrincipal,
    description: parque.metaDescription,
    url: parqueUrl(parque),
    address: {
      "@type": "PostalAddress",
      addressRegion: parque.departamentos.join(", "),
      addressCountry: "CO",
    },
    sameAs: parque.fuentes[0]?.url,
    isAccessibleForFree: false,
    subjectOf: {
      "@type": "WebPage",
      url: parqueUrl(parque),
      inLanguage: "es-CO",
      dateModified: parque.verificadoEl,
      publisher: { "@id": ORGANIZATION_ID },
    },
  };
}

export function buildParqueBreadcrumbJsonLd(
  parque: Parque,
): Record<string, unknown> {
  const trail = [
    { name: "Inicio", item: absoluteUrl("/") },
    { name: "Parques", item: absoluteUrl("/parques") },
    { name: parque.atractivoPrincipal, item: parqueUrl(parque) },
  ];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map(({ name, item }, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name,
      item,
    })),
  };
}

export function buildParquesListJsonLd(list: Parque[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": absoluteUrl("/parques"),
    name: "Guías de parques nacionales de Colombia",
    itemListElement: list.map((parque, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: parqueUrl(parque),
      name: parque.nombreOficial,
    })),
  };
}
